/**
 * Investigation Templates Page
 * Supports: 5-Why Analysis, Fishbone Diagram (Ishikawa), HFACS Analysis
 * Aligned with ICAO Annex 13 investigation standards
 * Features AI-assisted root cause analysis powered by GPT-5.2
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  HelpCircle, GitBranch, Layers, FileEdit, Plus, Search, ChevronRight,
  AlertTriangle, CheckCircle, Clock, Users, FileText, ArrowRight,
  Target, Lightbulb, List, Play, Save, X, ChevronDown, ChevronUp,
  Trash2, Edit2, Eye, Download, Clipboard, Sparkles, Loader2, Brain
} from 'lucide-react';

// Import extracted editor components
import { FiveWhyEditor, FishboneEditor, HFACSEditor } from '../components/investigations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Template type icons and colors
const TEMPLATE_CONFIG = {
  five_why: { icon: HelpCircle, color: 'blue', label: '5-Why Analysis' },
  fishbone: { icon: GitBranch, color: 'purple', label: 'Fishbone Diagram' },
  hfacs: { icon: Layers, color: 'amber', label: 'HFACS Analysis' },
  custom: { icon: FileEdit, color: 'gray', label: 'Custom Template' }
};

// Status badges
const STATUS_CONFIG = {
  draft: { color: 'bg-gray-500/20 text-gray-400', label: 'Draft' },
  in_progress: { color: 'bg-blue-500/20 text-blue-400', label: 'In Progress' },
  pending_review: { color: 'bg-amber-500/20 text-amber-400', label: 'Pending Review' },
  completed: { color: 'bg-emerald-500/20 text-emerald-400', label: 'Completed' },
  archived: { color: 'bg-slate-500/20 text-slate-400', label: 'Archived' }
};

// Fishbone category colors
const FISHBONE_COLORS = {
  man: 'bg-blue-500/20 border-blue-500/50 text-blue-300',
  machine: 'bg-gray-500/20 border-gray-500/50 text-gray-300',
  material: 'bg-orange-500/20 border-orange-500/50 text-orange-300',
  method: 'bg-purple-500/20 border-purple-500/50 text-purple-300',
  measurement: 'bg-green-500/20 border-green-500/50 text-green-300',
  mother_nature: 'bg-sky-500/20 border-sky-500/50 text-sky-300',
  software: 'bg-blue-500/20 border-blue-500/50 text-blue-300',
  hardware: 'bg-gray-500/20 border-gray-500/50 text-gray-300',
  environment: 'bg-green-500/20 border-green-500/50 text-green-300',
  liveware_self: 'bg-amber-500/20 border-amber-500/50 text-amber-300',
  liveware_others: 'bg-purple-500/20 border-purple-500/50 text-purple-300'
};

// HFACS level colors
const HFACS_LEVEL_COLORS = {
  unsafe_acts: 'bg-red-500/20 border-red-500/50',
  preconditions: 'bg-amber-500/20 border-amber-500/50',
  unsafe_supervision: 'bg-orange-500/20 border-orange-500/50',
  organizational: 'bg-purple-500/20 border-purple-500/50'
};

export default function InvestigationTemplatesPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [hfacsLevels, setHfacsLevels] = useState([]);
  const [fishboneCategories, setFishboneCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInvestigationDialog, setShowInvestigationDialog] = useState(false);
  const [showEditorDialog, setShowEditorDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedInvestigation, setSelectedInvestigation] = useState(null);
  
  // Form states
  const [newInvestigation, setNewInvestigation] = useState({
    incident_id: '',
    template_id: '',
    title: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [templatesRes, investigationsRes, statsRes, incidentsRes] = await Promise.all([
        fetch(`${API_URL}/api/investigations/templates`, { headers }),
        fetch(`${API_URL}/api/investigations/`, { headers }),
        fetch(`${API_URL}/api/investigations/stats/summary`, { headers }),
        fetch(`${API_URL}/api/incidents?limit=50`, { headers })
      ]);
      
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data);
      }
      
      if (investigationsRes.ok) {
        const data = await investigationsRes.json();
        setInvestigations(data);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      if (incidentsRes.ok) {
        const data = await incidentsRes.json();
        setIncidents(data);
      }
      
      // Fetch reference data
      const [hfacsRes, fishboneRes] = await Promise.all([
        fetch(`${API_URL}/api/investigations/reference/hfacs-levels`, { headers }),
        fetch(`${API_URL}/api/investigations/reference/fishbone-categories`, { headers })
      ]);
      
      if (hfacsRes.ok) setHfacsLevels(await hfacsRes.json());
      if (fishboneRes.ok) setFishboneCategories(await fishboneRes.json());
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSeedTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/investigations/templates/seed-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        toast.error('Failed to seed templates');
      }
    } catch (error) {
      toast.error('Error seeding templates');
    }
  };

  const handleCreateInvestigation = async () => {
    if (!newInvestigation.incident_id || !newInvestigation.template_id || !newInvestigation.title) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/investigations/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newInvestigation)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Investigation created successfully');
        setShowCreateDialog(false);
        setNewInvestigation({ incident_id: '', template_id: '', title: '' });
        fetchData();
        // Open editor for the new investigation
        setSelectedInvestigation(data);
        setShowEditorDialog(true);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create investigation');
      }
    } catch (error) {
      toast.error('Error creating investigation');
    }
  };

  const handleUpdateInvestigation = async (investigationId, updateData) => {
    try {
      const res = await fetch(`${API_URL}/api/investigations/${investigationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });
      
      if (res.ok) {
        const updated = await res.json();
        setSelectedInvestigation(updated);
        toast.success('Investigation saved');
        fetchData();
      } else {
        toast.error('Failed to update investigation');
      }
    } catch (error) {
      toast.error('Error updating investigation');
    }
  };

  const handleCompleteInvestigation = async (investigationId, rootCauses, recommendations) => {
    try {
      const res = await fetch(`${API_URL}/api/investigations/${investigationId}/complete?` + 
        new URLSearchParams({ root_causes: rootCauses, recommendations: recommendations }), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ root_causes: rootCauses, recommendations: recommendations })
      });
      
      if (res.ok) {
        toast.success('Investigation completed');
        setShowEditorDialog(false);
        fetchData();
      } else {
        toast.error('Failed to complete investigation');
      }
    } catch (error) {
      toast.error('Error completing investigation');
    }
  };

  const filteredInvestigations = investigations.filter(inv =>
    inv.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inv.investigation_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inv.incident_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading investigation templates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('investigation.title')}</h1>
          <p className="text-slate-400 mt-1">{t('investigation.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          {templates.length === 0 && (
            <Button onClick={handleSeedTemplates} variant="outline" className="border-emerald-500/50 text-emerald-400">
              <Play className="w-4 h-4 mr-2" />
              Seed Default Templates
            </Button>
          )}
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            {t('investigation.newTemplate')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <FileText className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                  <p className="text-xs text-slate-400">Total Investigations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.active}</p>
                  <p className="text-xs text-slate-400">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.by_status?.completed || 0}</p>
                  <p className="text-xs text-slate-400">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Layers className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.templates_count}</p>
                  <p className="text-xs text-slate-400">Templates</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.by_status?.pending_review || 0}</p>
                  <p className="text-xs text-slate-400">Pending Review</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="investigations">Investigations</TabsTrigger>
          <TabsTrigger value="methodology">Methodology Guide</TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {templates.map(template => {
              const config = TEMPLATE_CONFIG[template.template_type] || TEMPLATE_CONFIG.custom;
              const Icon = config.icon;
              
              return (
                <Card 
                  key={template.id}
                  className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-all cursor-pointer"
                  onClick={() => {
                    setSelectedTemplate(template);
                    setShowInvestigationDialog(true);
                  }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className={`p-2 rounded-lg bg-${config.color}-500/20`}>
                        <Icon className={`w-5 h-5 text-${config.color}-400`} />
                      </div>
                      {template.is_system && (
                        <Badge variant="outline" className="text-xs border-slate-600">System</Badge>
                      )}
                    </div>
                    <CardTitle className="text-white text-lg mt-3">{template.name}</CardTitle>
                    <CardDescription className="text-slate-400 text-sm line-clamp-2">
                      {template.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center justify-between">
                      <Badge className={`bg-${config.color}-500/20 text-${config.color}-400 border-0`}>
                        {config.label}
                      </Badge>
                      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                        Use <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {templates.length === 0 && (
              <div className="col-span-4 text-center py-12 text-slate-400">
                <Layers className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No templates found. Click "Seed Default Templates" to add the standard investigation methodologies.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Investigations Tab */}
        <TabsContent value="investigations" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Active Investigations</CardTitle>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search investigations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64 bg-slate-900/50 border-slate-600"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {filteredInvestigations.map(inv => {
                    const config = TEMPLATE_CONFIG[inv.template_type] || TEMPLATE_CONFIG.custom;
                    const Icon = config.icon;
                    const statusConfig = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
                    
                    return (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-slate-900/50 border border-slate-700 hover:border-slate-600 transition-all cursor-pointer"
                        onClick={() => {
                          setSelectedInvestigation(inv);
                          setShowEditorDialog(true);
                        }}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg bg-${config.color}-500/20`}>
                            <Icon className={`w-5 h-5 text-${config.color}-400`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">{inv.investigation_number}</span>
                              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                            </div>
                            <p className="text-sm text-slate-400 mt-1">{inv.title}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              Incident: {inv.incident_number} • Lead: {inv.lead_investigator_name || 'Unassigned'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  
                  {filteredInvestigations.length === 0 && (
                    <div className="text-center py-12 text-slate-400">
                      <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No investigations found</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Methodology Guide Tab */}
        <TabsContent value="methodology" className="mt-4">
          <div className="grid md:grid-cols-3 gap-6">
            {/* 5-Why Guide */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <div className="p-3 rounded-lg bg-blue-500/20 w-fit">
                  <HelpCircle className="w-6 h-6 text-blue-400" />
                </div>
                <CardTitle className="text-white mt-3">5-Why Analysis</CardTitle>
                <CardDescription>Iterative questioning to find root cause</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <h4 className="font-medium text-white">How to use:</h4>
                  <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
                    <li>State the problem clearly</li>
                    <li>Ask "Why did this happen?"</li>
                    <li>Record the answer</li>
                    <li>Ask "Why?" for each answer</li>
                    <li>Repeat until root cause found</li>
                  </ol>
                </div>
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <p className="text-xs text-blue-300">
                    <strong>Tip:</strong> The root cause is usually found between the 3rd and 5th "why"
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Fishbone Guide */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <div className="p-3 rounded-lg bg-purple-500/20 w-fit">
                  <GitBranch className="w-6 h-6 text-purple-400" />
                </div>
                <CardTitle className="text-white mt-3">Fishbone Diagram</CardTitle>
                <CardDescription>Cause-and-effect analysis (Ishikawa)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <h4 className="font-medium text-white">6M Categories:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['Man', 'Machine', 'Material', 'Method', 'Measurement', 'Mother Nature'].map(cat => (
                      <Badge key={cat} variant="outline" className="justify-center text-xs">{cat}</Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-white">SHELL Model (Aviation):</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['Software', 'Hardware', 'Environment', 'Liveware'].map(cat => (
                      <Badge key={cat} variant="outline" className="justify-center text-xs">{cat}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* HFACS Guide */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <div className="p-3 rounded-lg bg-amber-500/20 w-fit">
                  <Layers className="w-6 h-6 text-amber-400" />
                </div>
                <CardTitle className="text-white mt-3">HFACS Analysis</CardTitle>
                <CardDescription>Human Factors (Swiss Cheese Model)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <h4 className="font-medium text-white">4 Levels:</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="text-sm text-slate-300">Unsafe Acts</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-amber-500" />
                      <span className="text-sm text-slate-300">Preconditions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                      <span className="text-sm text-slate-300">Unsafe Supervision</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-purple-500" />
                      <span className="text-sm text-slate-300">Organizational Influences</span>
                    </div>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <p className="text-xs text-amber-300">
                    <strong>ICAO Aligned:</strong> Based on Reason's Swiss Cheese Model
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Investigation Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">New Investigation</DialogTitle>
            <DialogDescription>Create a new root cause investigation for an incident</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Select Incident *</Label>
              <Select
                value={newInvestigation.incident_id}
                onValueChange={(v) => setNewInvestigation(prev => ({ ...prev, incident_id: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-600">
                  <SelectValue placeholder="Select an incident..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {incidents.map(inc => (
                    <SelectItem key={inc.id} value={inc.id}>
                      {inc.report_number} - {inc.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Investigation Template *</Label>
              <Select
                value={newInvestigation.template_id}
                onValueChange={(v) => setNewInvestigation(prev => ({ ...prev, template_id: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-600">
                  <SelectValue placeholder="Select a template..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {templates.map(tmpl => (
                    <SelectItem key={tmpl.id} value={tmpl.id}>
                      {tmpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Investigation Title *</Label>
              <Input
                value={newInvestigation.title}
                onChange={(e) => setNewInvestigation(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter investigation title..."
                className="bg-slate-800 border-slate-600"
              />
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateInvestigation} className="bg-blue-600 hover:bg-blue-700">
                Create Investigation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Quick Start Dialog */}
      <Dialog open={showInvestigationDialog} onOpenChange={setShowInvestigationDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Start Investigation with Template</DialogTitle>
            <DialogDescription>
              {selectedTemplate?.name} - {selectedTemplate?.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Select Incident *</Label>
              <Select
                value={newInvestigation.incident_id}
                onValueChange={(v) => setNewInvestigation(prev => ({ 
                  ...prev, 
                  incident_id: v,
                  template_id: selectedTemplate?.id || '',
                  title: `${selectedTemplate?.name} - ${incidents.find(i => i.id === v)?.title || ''}`
                }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-600">
                  <SelectValue placeholder="Select an incident..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {incidents.map(inc => (
                    <SelectItem key={inc.id} value={inc.id}>
                      {inc.report_number} - {inc.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowInvestigationDialog(false)}>Cancel</Button>
              <Button 
                onClick={() => {
                  setShowInvestigationDialog(false);
                  setShowCreateDialog(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Investigation Editor Dialog */}
      <InvestigationEditorDialog
        open={showEditorDialog}
        onOpenChange={setShowEditorDialog}
        investigation={selectedInvestigation}
        hfacsLevels={hfacsLevels}
        fishboneCategories={fishboneCategories}
        onUpdate={handleUpdateInvestigation}
        onComplete={handleCompleteInvestigation}
      />
    </div>
  );
}

// Investigation Editor Component
function InvestigationEditorDialog({ 
  open, 
  onOpenChange, 
  investigation, 
  hfacsLevels, 
  fishboneCategories,
  onUpdate,
  onComplete 
}) {
  const { token } = useAuth();
  const [localData, setLocalData] = useState(null);
  const [activeSection, setActiveSection] = useState('analysis');
  
  useEffect(() => {
    if (investigation) {
      setLocalData(investigation);
    }
  }, [investigation]);
  
  if (!investigation || !localData) return null;
  
  const handleSave = () => {
    const updateData = {
      status: localData.status,
      five_why_data: localData.five_why_data,
      fishbone_data: localData.fishbone_data,
      hfacs_data: localData.hfacs_data,
      root_causes: localData.root_causes,
      recommended_actions: localData.recommended_actions
    };
    onUpdate(investigation.id, updateData);
  };
  
  const templateType = investigation.template_type;
  const statusConfig = STATUS_CONFIG[localData.status] || STATUS_CONFIG.draft;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-white flex items-center gap-2">
                {investigation.investigation_number}
                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
              </DialogTitle>
              <DialogDescription>{investigation.title}</DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleSave}>
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
              {localData.status !== 'completed' && (
                <Button 
                  size="sm" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    onComplete(
                      investigation.id, 
                      localData.root_causes || [], 
                      localData.recommended_actions || []
                    );
                  }}
                >
                  <CheckCircle className="w-4 h-4 mr-1" /> Complete
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>
        
        <ScrollArea className="h-[70vh] mt-4 pr-4">
          <Tabs value={activeSection} onValueChange={setActiveSection}>
            <TabsList className="bg-slate-800/50 mb-4">
              <TabsTrigger value="analysis">Analysis</TabsTrigger>
              <TabsTrigger value="findings">Findings</TabsTrigger>
              <TabsTrigger value="actions">Actions</TabsTrigger>
            </TabsList>
            
            <TabsContent value="analysis">
              {/* 5-Why Editor */}
              {templateType === 'five_why' && (
                <FiveWhyEditor 
                  data={localData.five_why_data || {}}
                  onChange={(data) => setLocalData(prev => ({ ...prev, five_why_data: data }))}
                  token={token}
                  onAISuggestApply={(suggestions) => {
                    // Apply AI suggestions to root causes
                    if (suggestions.root_cause_summary) {
                      setLocalData(prev => ({
                        ...prev,
                        root_causes: [...(prev.root_causes || []), suggestions.root_cause_summary]
                      }));
                    }
                    if (suggestions.recommended_actions) {
                      setLocalData(prev => ({
                        ...prev,
                        recommended_actions: [...(prev.recommended_actions || []), ...suggestions.recommended_actions]
                      }));
                    }
                  }}
                />
              )}
              
              {/* Fishbone Editor */}
              {templateType === 'fishbone' && (
                <FishboneEditor
                  data={localData.fishbone_data || {}}
                  categories={fishboneCategories}
                  onChange={(data) => setLocalData(prev => ({ ...prev, fishbone_data: data }))}
                  token={token}
                  onAISuggestApply={(suggestions) => {
                    if (suggestions.root_causes_identified) {
                      setLocalData(prev => ({
                        ...prev,
                        root_causes: [...(prev.root_causes || []), ...suggestions.root_causes_identified]
                      }));
                    }
                    if (suggestions.recommended_actions) {
                      setLocalData(prev => ({
                        ...prev,
                        recommended_actions: [...(prev.recommended_actions || []), ...suggestions.recommended_actions]
                      }));
                    }
                  }}
                />
              )}
              
              {/* HFACS Editor */}
              {templateType === 'hfacs' && (
                <HFACSEditor
                  data={localData.hfacs_data || {}}
                  levels={hfacsLevels}
                  onChange={(data) => setLocalData(prev => ({ ...prev, hfacs_data: data }))}
                  token={token}
                  onAISuggestApply={(suggestions) => {
                    if (suggestions.primary_factors) {
                      setLocalData(prev => ({
                        ...prev,
                        root_causes: [...(prev.root_causes || []), ...suggestions.primary_factors]
                      }));
                    }
                    if (suggestions.recommended_interventions) {
                      setLocalData(prev => ({
                        ...prev,
                        recommended_actions: [...(prev.recommended_actions || []), ...suggestions.recommended_interventions]
                      }));
                    }
                  }}
                />
              )}
            </TabsContent>
            
            <TabsContent value="findings">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Root Causes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(localData.root_causes || []).map((cause, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-red-400" />
                      <Input
                        value={cause}
                        onChange={(e) => {
                          const updated = [...(localData.root_causes || [])];
                          updated[idx] = e.target.value;
                          setLocalData(prev => ({ ...prev, root_causes: updated }));
                        }}
                        className="bg-slate-900/50 border-slate-600"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const updated = (localData.root_causes || []).filter((_, i) => i !== idx);
                          setLocalData(prev => ({ ...prev, root_causes: updated }));
                        }}
                      >
                        <X className="w-4 h-4 text-slate-400" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setLocalData(prev => ({
                        ...prev,
                        root_causes: [...(prev.root_causes || []), '']
                      }));
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Root Cause
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="actions">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Recommended Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(localData.recommended_actions || []).map((action, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-400" />
                      <Input
                        value={action}
                        onChange={(e) => {
                          const updated = [...(localData.recommended_actions || [])];
                          updated[idx] = e.target.value;
                          setLocalData(prev => ({ ...prev, recommended_actions: updated }));
                        }}
                        className="bg-slate-900/50 border-slate-600"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const updated = (localData.recommended_actions || []).filter((_, i) => i !== idx);
                          setLocalData(prev => ({ ...prev, recommended_actions: updated }));
                        }}
                      >
                        <X className="w-4 h-4 text-slate-400" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setLocalData(prev => ({
                        ...prev,
                        recommended_actions: [...(prev.recommended_actions || []), '']
                      }));
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Action
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

